import { useState } from 'react';
import { Link } from 'react-router-dom';

export default function FooterSection() {
  const [email, setEmail] = useState('');
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [submitMessage, setSubmitMessage] = useState('');

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget as HTMLFormElement;
    const formData = new FormData(form);

    const hpValue = (formData.get('company_alt') as string || '').trim();
    if (hpValue) {
      setSubmitStatus('success');
      setSubmitMessage('Thanks for subscribing!');
      setEmail('');
      return;
    }

    try {
      const response = await fetch('https://readdy.ai/api/form/d992r02g3iubr452ii5g', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: new URLSearchParams({
          email: formData.get('email') as string,
        }).toString(),
      });

      const responseText = await response.text();
      let parsed: { code?: string; meta?: { message?: string; detail?: string }; message?: string } | null = null;

      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }

      if (response.ok && parsed?.code === 'OK') {
        setSubmitStatus('success');
        setSubmitMessage('Thanks for subscribing! Stay tuned for the latest updates.');
        setEmail('');
      } else {
        const serverMsg = parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText || '';
        if (serverMsg.toLowerCase().includes('spam') || serverMsg.toLowerCase().includes('form data is spam')) {
          setSubmitStatus('error');
          setSubmitMessage('Submission could not be processed. Please try again.');
        } else {
          setSubmitStatus('error');
          setSubmitMessage(serverMsg || 'Something went wrong. Please try again later.');
        }
      }
    } catch {
      setSubmitStatus('error');
      setSubmitMessage('Network error. Please check your connection and try again.');
    }
  };

  return (
    <footer className="bg-foreground-950 rounded-t-[40px] pt-16 md:pt-20 pb-8">
      <div className="max-w-[1200px] mx-auto px-4 md:px-6">
        <div className="flex flex-col lg:flex-row gap-12 lg:gap-24 mb-16">
          <div className="flex-1">
            <Link to="/" className="text-background-50 font-heading text-2xl font-bold tracking-tight whitespace-nowrap cursor-pointer">
              Hipobuy
            </Link>
            <p className="text-foreground-300 text-sm mt-4 max-w-sm leading-relaxed">
              The most comprehensive English information hub about Hipobuy Spreadsheet — helping shoppers discover, compare, and research products with organized spreadsheet tools.
            </p>
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-3 gap-8 flex-[2]">
            <div>
              <h4 className="text-background-50 font-heading text-base font-bold mb-4">Quick Links</h4>
              <ul className="space-y-2.5">
                <li>
                  <Link to="/" className="text-foreground-400 text-sm hover:text-background-50 transition-colors cursor-pointer">Home</Link>
                </li>
                <li>
                  <a href="#categories" className="text-foreground-400 text-sm hover:text-background-50 transition-colors cursor-pointer">Categories</a>
                </li>
                <li>
                  <Link to="/hipobuy-spreadsheet-guide" className="text-foreground-400 text-sm hover:text-background-50 transition-colors cursor-pointer">Complete Guide</Link>
                </li>
                <li>
                  <Link to="/kaspreadsheet-shopping-guide" className="text-foreground-400 text-sm hover:text-background-50 transition-colors cursor-pointer">Shopping Guide</Link>
                </li>
                <li>
                  <a href="#about" className="text-foreground-400 text-sm hover:text-background-50 transition-colors cursor-pointer">About</a>
                </li>
              </ul>
            </div>

            <div>
              <h4 className="text-background-50 font-heading text-base font-bold mb-4">Top Categories</h4>
              <ul className="space-y-2.5">
                <li>
                  <a href="https://kaspreadsheet.com/category-911-Shoes.html" target="_blank" rel="noopener nofollow" className="text-foreground-400 text-sm hover:text-background-50 transition-colors cursor-pointer">Shoes</a>
                </li>
                <li>
                  <a href="https://kaspreadsheet.com/search-912/name/Shoes.html" target="_blank" rel="noopener nofollow" className="text-foreground-400 text-sm hover:text-background-50 transition-colors cursor-pointer">Hoodies / Sweaters</a>
                </li>
                <li>
                  <a href="https://kaspreadsheet.com/search-913/name/Shoes.html" target="_blank" rel="noopener nofollow" className="text-foreground-400 text-sm hover:text-background-50 transition-colors cursor-pointer">T-Shirts</a>
                </li>
                <li>
                  <a href="https://kaspreadsheet.com/search-914/name/Shoes.html" target="_blank" rel="noopener nofollow" className="text-foreground-400 text-sm hover:text-background-50 transition-colors cursor-pointer">Jackets</a>
                </li>
              </ul>
            </div>

            <div>
              <h4 className="text-background-50 font-heading text-base font-bold mb-4">Resources</h4>
              <ul className="space-y-2.5">
                <li>
                  <Link to="/hipobuy-spreadsheet-guide" className="text-foreground-400 text-sm hover:text-background-50 transition-colors cursor-pointer">Beginner Guide</Link>
                </li>
                <li>
                  <Link to="/kaspreadsheet-shopping-guide" className="text-foreground-400 text-sm hover:text-background-50 transition-colors cursor-pointer">Shopping Guide</Link>
                </li>
                <li>
                  <Link to="/kaspreadsheet-product-research" className="text-foreground-400 text-sm hover:text-background-50 transition-colors cursor-pointer">Research Tips</Link>
                </li>
                <li>
                  <Link to="/kaspreadsheet-vs-traditional" className="text-foreground-400 text-sm hover:text-background-50 transition-colors cursor-pointer">Comparison</Link>
                </li>
                <li>
                  <a href="https://kaspreadsheet.com" target="_blank" rel="noopener nofollow" className="text-foreground-400 text-sm hover:text-background-50 transition-colors cursor-pointer">Main Website</a>
                </li>
              </ul>
            </div>
          </div>
        </div>

        <div className="flex flex-col lg:flex-row items-start lg:items-center justify-between gap-8 pt-8 border-t border-foreground-800/50">
          <div className="flex items-center gap-6">
            <a href="https://kaspreadsheet.com" target="_blank" rel="noopener nofollow" className="text-foreground-400 hover:text-background-50 transition-colors cursor-pointer">
              <i className="ri-global-line text-lg w-5 h-5 flex items-center justify-center"></i>
            </a>
          </div>

          <div className="flex-1 max-w-md w-full lg:mx-auto">
            <form onSubmit={handleSubmit} className="flex gap-2" data-readdy-form="">
              <input type="email" name="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="Your email for updates" required className="flex-1 bg-foreground-800 border border-foreground-700 rounded-xl px-4 py-3 text-sm text-background-50 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 transition-colors" />
              <button type="submit" className="bg-accent-500 hover:bg-accent-600 text-background-50 px-6 py-3 rounded-xl text-sm font-semibold whitespace-nowrap cursor-pointer transition-colors">
                Subscribe
              </button>
              <input type="text" name="company_alt" tabIndex={-1} autoComplete="off" aria-hidden="true" readOnly className="newsletter-hp" />
            </form>
            {submitStatus === 'success' && (
              <p className="text-accent-400 text-xs mt-2">{submitMessage}</p>
            )}
            {submitStatus === 'error' && (
              <p className="text-red-400 text-xs mt-2">{submitMessage}</p>
            )}
          </div>

          <div>
            <p className="text-foreground-500 text-xs whitespace-nowrap">
              &copy; {new Date().getFullYear()} Hipobuy. All rights reserved.
            </p>
          </div>
        </div>

        <div className="mt-8 pt-6 border-t border-foreground-800/30">
          <p className="text-foreground-500 text-xs leading-relaxed">
            <strong>Important Safety Information:</strong> This website provides educational resources and research guides about shopping spreadsheets. Users should always verify product information, seller reputation, payment security, and website authenticity before making any purchase decision. We do not guarantee third-party sellers or external platforms.
          </p>
        </div>
      </div>
    </footer>
  );
}